<script setup lang="ts">
import type { Job } from '@/core/types'
import { formatBytes } from '@/core/files'

defineProps<{ jobs: Job[]; selectedId: string | null }>()
const emit = defineEmits<{ select: [string]; remove: [string] }>()

const STATUS_LABEL: Record<Job['status'], string> = {
  queued: 'Queued',
  detecting: 'Detecting',
  converting: 'Converting',
  done: 'Done',
  error: 'Failed',
}
</script>

<template>
  <ul v-if="jobs.length" class="space-y-1" role="list">
    <li v-for="job in jobs" :key="job.id">
      <div
        class="group flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors"
        :class="
          job.id === selectedId
            ? 'border-sky-500 bg-sky-50 dark:bg-sky-950/40'
            : 'border-transparent hover:bg-slate-100 dark:hover:bg-slate-800'
        "
      >
        <button
          type="button"
          class="min-w-0 flex-1 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
          :aria-current="job.id === selectedId"
          @click="emit('select', job.id)"
        >
          <span class="block truncate text-sm font-medium" :title="job.name">{{ job.name }}</span>
          <span class="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <span
              class="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
              :class="{
                'bg-emerald-500': job.status === 'done',
                'bg-rose-500': job.status === 'error',
                'animate-pulse bg-sky-500': job.status === 'converting' || job.status === 'queued',
              }"
            />
            <span>{{ STATUS_LABEL[job.status] }}</span>
            <span aria-hidden="true">·</span>
            <span>{{ formatBytes(job.size) }}</span>
            <template v-if="job.format">
              <span aria-hidden="true">·</span>
              <span class="uppercase">{{ job.format }}</span>
            </template>
          </span>
        </button>

        <button
          type="button"
          class="shrink-0 rounded p-1 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-rose-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
          :aria-label="`Remove ${job.name}`"
          @click="emit('remove', job.id)"
        >
          <svg
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </li>
  </ul>
</template>
